import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronRight, HelpCircle, Info, Pencil } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CompactTranslationEditor } from "@/components/compact-translation-editor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/translations")({
	component: TranslationsPage,
});

type ResourceStatus = "active" | "archived" | "deleted";

type Plan = {
	id: string;
	name: string;
	description: string | null;
	status: ResourceStatus;
	translations: Record<string, Record<string, string>>;
};

type ItemPrice = {
	id: string;
	name: string;
	planId: string | null;
	planName: string | null;
	status: ResourceStatus;
	translations: Record<string, Record<string, string>>;
};

type Feature = {
	id: string;
	name: string;
	description: string | null;
	status: ResourceStatus;
	category: string | null;
	translations: Record<string, Record<string, string>>;
};

type Addon = {
	id: string;
	name: string;
	description: string | null;
	status: ResourceStatus;
	translations: Record<string, Record<string, string>>;
};

type EntitlementFeature = {
	featureId: string;
	syncedName: string;
	translations: Record<string, Record<string, string>>;
};

type EditingState = {
	resourceType:
		| "plan"
		| "item_price"
		| "plan_feature"
		| "plan_addon"
		| "entitlement_feature";
	resourceId: string;
	translations: Record<string, Record<string, string>>;
	displayName: string;
};

type ClientWithPricing = typeof client & {
	admin: typeof client.admin & {
		pricing: typeof client.admin.pricing & {
			listPlans: (input: object) => Promise<{ plans: Plan[] }>;
			listItemPrices: (input: object) => Promise<{ itemPrices: ItemPrice[] }>;
			listFeatures: (input: object) => Promise<{ features: Feature[] }>;
			listAddons: (input: object) => Promise<{ addons: Addon[] }>;
			listEntitlementFeatures: (
				input: object,
			) => Promise<{ features: EntitlementFeature[] }>;
			upsertEntitlementFeatureTranslations: (input: {
				featureId: string;
				translations: Record<string, Record<string, string>>;
			}) => Promise<{ success: boolean; message: string; count: number }>;
		};
		translations: {
			upsertTranslations: (input: {
				resourceType: string;
				resourceId: string;
				translations: Record<string, Record<string, string>>;
			}) => Promise<{ success: boolean; message: string; count: number }>;
		};
	};
};

type StatusBadgeProps = {
	status: ResourceStatus;
};

const StatusBadge = ({ status }: StatusBadgeProps) => {
	const { t } = useTranslation();
	if (status === "archived") {
		return (
			<Badge variant="outline" className="border-muted-foreground/40">
				{t("admin:translations.status_archived")}
			</Badge>
		);
	}
	return (
		<Badge variant="secondary">{t("admin:translations.status_active")}</Badge>
	);
};

type TranslationsPreviewCellProps = {
	text: string;
};

// The translations preview can be long; constrain + truncate it so the column
// never forces the table wider than its container (overflow fix). Full text is
// available on hover via the native title tooltip.
const TranslationsPreviewCell = ({ text }: TranslationsPreviewCellProps) => (
	<span
		className="block max-w-[18rem] truncate text-muted-foreground text-xs"
		title={text}
	>
		{text}
	</span>
);

type UsageHintProps = {
	titleKey: string;
	bodyKey: string;
};

const UsageHint = ({ titleKey, bodyKey }: UsageHintProps) => {
	const { t } = useTranslation();
	return (
		<div className="flex gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900 dark:bg-blue-950/40">
			<Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
			<div className="space-y-1">
				<p className="font-medium text-blue-900 dark:text-blue-100">
					{t(titleKey)}
				</p>
				<p className="text-blue-900/80 dark:text-blue-100/80">{t(bodyKey)}</p>
			</div>
		</div>
	);
};

// Every usage hint shown across the tabs, consolidated so the page header's
// help button can surface them all in one dialog instead of pushing the tables
// down on every tab.
const USAGE_HINTS: ReadonlyArray<{ titleKey: string; bodyKey: string }> = [
	{
		titleKey: "admin:translations.plan_translations_usage_title",
		bodyKey: "admin:translations.plan_translations_usage",
	},
	{
		titleKey: "admin:translations.item_price_translations_usage_title",
		bodyKey: "admin:translations.item_price_translations_usage",
	},
	{
		titleKey: "admin:translations.feature_translations_usage_title",
		bodyKey: "admin:translations.feature_translations_usage",
	},
	{
		titleKey: "admin:translations.addon_translations_usage_title",
		bodyKey: "admin:translations.addon_translations_usage",
	},
	{
		titleKey: "admin:translations.entitlement_feature_translations_usage_title",
		bodyKey: "admin:translations.entitlement_feature_translations_usage",
	},
];

const TranslationsHelpDialog = () => {
	const { t } = useTranslation();
	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					aria-label={t("admin:translations.help_title")}
					title={t("admin:translations.help_title")}
				>
					<HelpCircle className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{t("admin:translations.help_title")}</DialogTitle>
					<DialogDescription>
						{t("admin:translations.help_description")}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-3">
					{USAGE_HINTS.map((hint) => (
						<UsageHint
							key={hint.titleKey}
							titleKey={hint.titleKey}
							bodyKey={hint.bodyKey}
						/>
					))}
				</div>
			</DialogContent>
		</Dialog>
	);
};

type EditorPanelProps = {
	editingItem: EditingState | null;
	resourceType: EditingState["resourceType"];
	onSave: (translations: Record<string, Record<string, string>>) => void;
	onCancel: () => void;
	isLoading: boolean;
};

const EditorPanel = ({
	editingItem,
	resourceType,
	onSave,
	onCancel,
	isLoading,
}: EditorPanelProps) => {
	const { t } = useTranslation();
	if (!editingItem || editingItem.resourceType !== resourceType) {
		return null;
	}
	return (
		<div className="border-t bg-muted/30 p-4">
			<div className="rounded-lg border bg-card p-6 shadow-sm">
				<h2 className="mb-4 font-semibold text-lg">
					{t("admin:translations.edit_translation")} - {editingItem.displayName}
				</h2>
				<CompactTranslationEditor
					key={`${editingItem.resourceType}:${editingItem.resourceId}`}
					translations={editingItem.translations}
					onSave={onSave}
					onCancel={onCancel}
					isLoading={isLoading}
					availableKeys={
						resourceType === "entitlement_feature"
							? ["name"]
							: ["name", "description"]
					}
				/>
			</div>
		</div>
	);
};

function TranslationsPage() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [editingItem, setEditingItem] = useState<EditingState | null>(null);
	const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());

	const togglePlanExpanded = (planId: string) => {
		setExpandedPlans((prev) => {
			const next = new Set(prev);
			if (next.has(planId)) {
				next.delete(planId);
			} else {
				next.add(planId);
			}
			return next;
		});
	};

	// Fetch all resource types
	const { data: plansData, isLoading: plansLoading } = useQuery<{
		plans: Plan[];
	}>({
		queryKey: ["admin", "pricing", "listPlans"],
		queryFn: async () => {
			return await (client as ClientWithPricing).admin.pricing.listPlans({});
		},
		staleTime: 2 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	const { data: itemPricesData, isLoading: itemPricesLoading } = useQuery<{
		itemPrices: ItemPrice[];
	}>({
		queryKey: ["admin", "pricing", "listItemPrices"],
		queryFn: async () => {
			return await (client as ClientWithPricing).admin.pricing.listItemPrices(
				{},
			);
		},
		staleTime: 2 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	const { data: featuresData, isLoading: featuresLoading } = useQuery<{
		features: Feature[];
	}>({
		queryKey: ["admin", "pricing", "listFeatures"],
		queryFn: async () => {
			return await (client as ClientWithPricing).admin.pricing.listFeatures({});
		},
		staleTime: 2 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	const { data: addonsData, isLoading: addonsLoading } = useQuery<{
		addons: Addon[];
	}>({
		queryKey: ["admin", "pricing", "listAddons"],
		queryFn: async () => {
			return await (client as ClientWithPricing).admin.pricing.listAddons({});
		},
		staleTime: 2 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	const {
		data: entitlementFeaturesData,
		isLoading: entitlementFeaturesLoading,
	} = useQuery<{ features: EntitlementFeature[] }>({
		queryKey: ["admin", "pricing", "listEntitlementFeatures"],
		queryFn: async () => {
			return await (
				client as ClientWithPricing
			).admin.pricing.listEntitlementFeatures({});
		},
		staleTime: 2 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	// Upsert translation mutation
	const upsertMutation = useMutation({
		mutationFn: async (input: {
			resourceType: "plan" | "item_price" | "plan_feature" | "plan_addon";
			resourceId: string;
			translations: Record<string, Record<string, string>>;
		}) => {
			return await (
				client as ClientWithPricing
			).admin.translations.upsertTranslations(input);
		},
		onSuccess: async () => {
			toast.success(t("admin:translations.save_success"));
			// Invalidate all translation queries
			await queryClient.invalidateQueries({
				queryKey: ["admin", "pricing"],
			});
			setEditingItem(null);
		},
		onError: (error) => {
			toast.error(error.message || t("admin:translations.save_error"));
		},
	});

	// Upsert mutation for entitlement feature translations (separate from UUID-based upsert)
	const upsertEntitlementMutation = useMutation({
		mutationFn: async (input: {
			featureId: string;
			translations: Record<string, Record<string, string>>;
		}) => {
			return await (
				client as ClientWithPricing
			).admin.pricing.upsertEntitlementFeatureTranslations(input);
		},
		onSuccess: async () => {
			toast.success(t("admin:translations.save_success"));
			await queryClient.invalidateQueries({
				queryKey: ["admin", "pricing", "listEntitlementFeatures"],
			});
			setEditingItem(null);
		},
		onError: (error) => {
			toast.error(error.message || t("admin:translations.save_error"));
		},
	});

	const startEditingPlan = (item: Plan) => {
		setEditingItem({
			resourceType: "plan",
			resourceId: item.id,
			translations: item.translations || {},
			displayName: item.name,
		});
	};

	const startEditingItemPrice = (item: ItemPrice) => {
		setEditingItem({
			resourceType: "item_price",
			resourceId: item.id,
			translations: item.translations || {},
			displayName: item.planName
				? `${item.planName} — ${item.name}`
				: item.name,
		});
	};

	const startEditingFeature = (item: Feature) => {
		setEditingItem({
			resourceType: "plan_feature",
			resourceId: item.id,
			translations: item.translations || {},
			displayName: item.name,
		});
	};

	const startEditingAddon = (item: Addon) => {
		setEditingItem({
			resourceType: "plan_addon",
			resourceId: item.id,
			translations: item.translations || {},
			displayName: item.name,
		});
	};

	const startEditingEntitlementFeature = (item: EntitlementFeature) => {
		setEditingItem({
			resourceType: "entitlement_feature",
			resourceId: item.featureId,
			translations: item.translations || {},
			displayName: `${item.syncedName} (${item.featureId})`,
		});
	};

	const cancelEditing = () => {
		setEditingItem(null);
	};

	const handleSave = (translations: Record<string, Record<string, string>>) => {
		if (!editingItem) {
			return;
		}

		if (editingItem.resourceType === "entitlement_feature") {
			upsertEntitlementMutation.mutate({
				featureId: editingItem.resourceId,
				translations,
			});
		} else {
			upsertMutation.mutate({
				resourceType: editingItem.resourceType,
				resourceId: editingItem.resourceId,
				translations,
			});
		}
	};

	const formatTranslations = (
		translations: Record<string, Record<string, string>>,
	) => {
		if (!translations || Object.keys(translations).length === 0) {
			return t("admin:translations.no_translations");
		}

		return Object.entries(translations)
			.map(([lang, content]) => {
				const allText = Object.values(content)
					.join(" ")
					.replace(/<[^>]*>/g, "")
					.substring(0, 50);
				return `${lang}: ${allText}...`;
			})
			.join(", ");
	};

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between gap-3">
				<div>
					<h1 className="font-bold text-3xl">
						{t("admin:translations.title")}
					</h1>
					<p className="text-muted-foreground">
						{t("admin:translations.subtitle")}
					</p>
				</div>
				<TranslationsHelpDialog />
			</div>

			<Tabs defaultValue="plans" className="w-full">
				<TabsList>
					<TabsTrigger value="plans">
						{t("admin:translations.plans")}
					</TabsTrigger>
					<TabsTrigger value="features">
						{t("admin:translations.features")}
					</TabsTrigger>
					<TabsTrigger value="addons">
						{t("admin:translations.addons")}
					</TabsTrigger>
					<TabsTrigger value="entitlementFeatures">
						{t("admin:translations.entitlement_features")}
					</TabsTrigger>
				</TabsList>

				{/* Plans + Item Prices Tab (combined, expandable) */}
				<TabsContent value="plans" className="mt-6">
					<div className="rounded-lg border bg-card shadow-sm">
						<div className="border-b p-4">
							<h2 className="font-semibold text-lg">
								{t("admin:translations.plan_translations")}
							</h2>
							<p className="text-muted-foreground text-sm">
								{t("admin:translations.plan_item_prices_combined_description")}
							</p>
						</div>

						<div className="p-4">
							{plansLoading || itemPricesLoading ? (
								<div className="space-y-3">
									{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
										(id) => (
											<Skeleton key={id} className="h-20 w-full" />
										),
									)}
								</div>
							) : plansData && plansData.plans.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="w-8" />
											<TableHead>{t("admin:translations.name")}</TableHead>
											<TableHead>
												{t("admin:translations.description")}
											</TableHead>
											<TableHead>{t("admin:translations.status")}</TableHead>
											<TableHead>
												{t("admin:translations.translations")}
											</TableHead>
											<TableHead className="text-right">
												{t("admin:translations.actions")}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{plansData.plans.map((plan) => {
											const isExpanded = expandedPlans.has(plan.id);
											const planItemPrices =
												itemPricesData?.itemPrices.filter(
													(ip) => ip.planId === plan.id,
												) ?? [];
											return (
												<Fragment key={plan.id}>
													<TableRow
														className={`cursor-pointer ${
															plan.status === "archived" ? "opacity-60" : ""
														}`}
														onClick={() => togglePlanExpanded(plan.id)}
													>
														<TableCell>
															<button
																type="button"
																aria-label={t(
																	"admin:translations.expand_plan_item_prices",
																)}
																aria-expanded={isExpanded}
																onClick={(e) => {
																	e.stopPropagation();
																	togglePlanExpanded(plan.id);
																}}
																className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
															>
																<ChevronRight
																	className={`h-4 w-4 transition-transform ${
																		isExpanded ? "rotate-90" : ""
																	}`}
																/>
															</button>
														</TableCell>
														<TableCell className="font-medium">
															{plan.name}
														</TableCell>
														<TableCell>
															<div
																className="max-w-[16rem] truncate text-muted-foreground text-xs"
																title={plan.description || undefined}
															>
																{plan.description || "-"}
															</div>
														</TableCell>
														<TableCell>
															<StatusBadge status={plan.status} />
														</TableCell>
														<TableCell>
															<TranslationsPreviewCell
																text={formatTranslations(plan.translations)}
															/>
														</TableCell>
														<TableCell className="text-right">
															<Button
																variant="outline"
																size="icon"
																onClick={(e) => {
																	e.stopPropagation();
																	startEditingPlan(plan);
																}}
																disabled={upsertMutation.isPending}
															>
																<Pencil className="h-4 w-4" />
																<span className="sr-only">
																	{t("common:edit")}
																</span>
															</Button>
														</TableCell>
													</TableRow>
													{isExpanded && (
														<TableRow className="bg-muted/30 hover:bg-muted/30">
															<TableCell />
															<TableCell colSpan={5} className="p-0">
																<div className="p-4">
																	<h3 className="mb-2 font-semibold text-sm">
																		{t("admin:translations.item_prices")}
																	</h3>
																	{planItemPrices.length === 0 ? (
																		<p className="py-2 text-muted-foreground text-sm">
																			{t(
																				"admin:translations.no_item_prices_for_plan",
																			)}
																		</p>
																	) : (
																		<Table>
																			<TableHeader>
																				<TableRow>
																					<TableHead>
																						{t("admin:translations.name")}
																					</TableHead>
																					<TableHead>
																						{t("admin:translations.status")}
																					</TableHead>
																					<TableHead>
																						{t(
																							"admin:translations.translations",
																						)}
																					</TableHead>
																					<TableHead className="text-right">
																						{t("admin:translations.actions")}
																					</TableHead>
																				</TableRow>
																			</TableHeader>
																			<TableBody>
																				{planItemPrices.map((ip) => (
																					<TableRow
																						key={ip.id}
																						className={
																							ip.status === "archived"
																								? "opacity-60"
																								: undefined
																						}
																					>
																						<TableCell className="font-medium text-sm">
																							{ip.name}
																						</TableCell>
																						<TableCell>
																							<StatusBadge status={ip.status} />
																						</TableCell>
																						<TableCell>
																							<TranslationsPreviewCell
																								text={formatTranslations(
																									ip.translations,
																								)}
																							/>
																						</TableCell>
																						<TableCell className="text-right">
																							<Button
																								variant="outline"
																								size="icon"
																								onClick={() =>
																									startEditingItemPrice(ip)
																								}
																								disabled={
																									upsertMutation.isPending
																								}
																							>
																								<Pencil className="h-4 w-4" />
																								<span className="sr-only">
																									{t("common:edit")}
																								</span>
																							</Button>
																						</TableCell>
																					</TableRow>
																				))}
																			</TableBody>
																		</Table>
																	)}
																</div>
															</TableCell>
														</TableRow>
													)}
												</Fragment>
											);
										})}
									</TableBody>
								</Table>
							) : (
								<div className="py-12 text-center">
									<p className="text-muted-foreground">
										{t("admin:translations.no_resources")}
									</p>
								</div>
							)}
						</div>
						<EditorPanel
							editingItem={editingItem}
							resourceType="plan"
							onSave={handleSave}
							onCancel={cancelEditing}
							isLoading={upsertMutation.isPending}
						/>
						<EditorPanel
							editingItem={editingItem}
							resourceType="item_price"
							onSave={handleSave}
							onCancel={cancelEditing}
							isLoading={upsertMutation.isPending}
						/>
					</div>
				</TabsContent>

				{/* Features Tab */}
				<TabsContent value="features" className="mt-6">
					<div className="rounded-lg border bg-card shadow-sm">
						<div className="border-b p-4">
							<h2 className="font-semibold text-lg">
								{t("admin:translations.feature_translations")}
							</h2>
							<p className="text-muted-foreground text-sm">
								{t("admin:translations.feature_translations_description")}
							</p>
						</div>

						<div className="p-4">
							{featuresLoading ? (
								<div className="space-y-3">
									{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
										(id) => (
											<Skeleton key={id} className="h-20 w-full" />
										),
									)}
								</div>
							) : featuresData && featuresData.features.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("admin:translations.name")}</TableHead>
											<TableHead>{t("admin:translations.category")}</TableHead>
											<TableHead>{t("admin:translations.status")}</TableHead>
											<TableHead>
												{t("admin:translations.translations")}
											</TableHead>
											<TableHead className="text-right">
												{t("admin:translations.actions")}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{featuresData.features.map((item) => (
											<TableRow
												key={item.id}
												className={
													item.status === "archived" ? "opacity-60" : undefined
												}
											>
												<TableCell className="font-medium">
													{item.name}
												</TableCell>
												<TableCell>
													<div className="text-muted-foreground text-xs">
														{item.category || "-"}
													</div>
												</TableCell>
												<TableCell>
													<StatusBadge status={item.status} />
												</TableCell>
												<TableCell>
													<TranslationsPreviewCell
														text={formatTranslations(item.translations)}
													/>
												</TableCell>
												<TableCell className="text-right">
													<Button
														variant="outline"
														size="icon"
														onClick={() => startEditingFeature(item)}
														disabled={upsertMutation.isPending}
													>
														<Pencil className="h-4 w-4" />
														<span className="sr-only">{t("common:edit")}</span>
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="py-12 text-center">
									<p className="text-muted-foreground">
										{t("admin:translations.no_resources")}
									</p>
								</div>
							)}
						</div>
						<EditorPanel
							editingItem={editingItem}
							resourceType="plan_feature"
							onSave={handleSave}
							onCancel={cancelEditing}
							isLoading={upsertMutation.isPending}
						/>
					</div>
				</TabsContent>

				{/* Addons Tab */}
				<TabsContent value="addons" className="mt-6">
					<div className="rounded-lg border bg-card shadow-sm">
						<div className="border-b p-4">
							<h2 className="font-semibold text-lg">
								{t("admin:translations.addon_translations")}
							</h2>
							<p className="text-muted-foreground text-sm">
								{t("admin:translations.addon_translations_description")}
							</p>
						</div>

						<div className="p-4">
							{addonsLoading ? (
								<div className="space-y-3">
									{Array.from({ length: 5 }, (_, i) => `skeleton-${i}`).map(
										(id) => (
											<Skeleton key={id} className="h-20 w-full" />
										),
									)}
								</div>
							) : addonsData && addonsData.addons.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("admin:translations.name")}</TableHead>
											<TableHead>{t("admin:translations.type")}</TableHead>
											<TableHead>{t("admin:translations.status")}</TableHead>
											<TableHead>
												{t("admin:translations.translations")}
											</TableHead>
											<TableHead className="text-right">
												{t("admin:translations.actions")}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{addonsData.addons.map((item) => (
											<TableRow
												key={item.id}
												className={
													item.status === "archived" ? "opacity-60" : undefined
												}
											>
												<TableCell className="font-medium">
													{item.name}
												</TableCell>
												<TableCell>
													<div
														className="max-w-[16rem] truncate text-muted-foreground text-xs"
														title={item.description || undefined}
													>
														{item.description || "-"}
													</div>
												</TableCell>
												<TableCell>
													<StatusBadge status={item.status} />
												</TableCell>
												<TableCell>
													<TranslationsPreviewCell
														text={formatTranslations(item.translations)}
													/>
												</TableCell>
												<TableCell className="text-right">
													<Button
														variant="outline"
														size="icon"
														onClick={() => startEditingAddon(item)}
														disabled={upsertMutation.isPending}
													>
														<Pencil className="h-4 w-4" />
														<span className="sr-only">{t("common:edit")}</span>
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="py-12 text-center">
									<p className="text-muted-foreground">
										{t("admin:translations.no_resources")}
									</p>
								</div>
							)}
						</div>
						<EditorPanel
							editingItem={editingItem}
							resourceType="plan_addon"
							onSave={handleSave}
							onCancel={cancelEditing}
							isLoading={upsertMutation.isPending}
						/>
					</div>
				</TabsContent>

				{/* Entitlement Features Tab */}
				<TabsContent value="entitlementFeatures" className="mt-6">
					<div className="rounded-lg border bg-card shadow-sm">
						<div className="border-b p-4">
							<h2 className="font-semibold text-lg">
								{t("admin:translations.entitlement_feature_translations")}
							</h2>
							<p className="text-muted-foreground text-sm">
								{t(
									"admin:translations.entitlement_feature_translations_description",
								)}
							</p>
						</div>

						<div className="p-4">
							{entitlementFeaturesLoading ? (
								<div className="space-y-3">
									{Array.from({ length: 3 }, (_, i) => `skeleton-${i}`).map(
										(id) => (
											<Skeleton key={id} className="h-20 w-full" />
										),
									)}
								</div>
							) : entitlementFeaturesData &&
								entitlementFeaturesData.features.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>
												{t("admin:translations.feature_id")}
											</TableHead>
											<TableHead>
												{t("admin:translations.synced_name")}
											</TableHead>
											<TableHead>
												{t("admin:translations.translations")}
											</TableHead>
											<TableHead className="text-right">
												{t("admin:translations.actions")}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{entitlementFeaturesData.features.map((item) => (
											<TableRow key={item.featureId}>
												<TableCell className="font-mono text-sm">
													{item.featureId}
												</TableCell>
												<TableCell className="font-medium">
													{item.syncedName}
												</TableCell>
												<TableCell>
													<TranslationsPreviewCell
														text={formatTranslations(item.translations)}
													/>
												</TableCell>
												<TableCell className="text-right">
													<Button
														variant="outline"
														size="icon"
														onClick={() => startEditingEntitlementFeature(item)}
														disabled={upsertEntitlementMutation.isPending}
													>
														<Pencil className="h-4 w-4" />
														<span className="sr-only">{t("common:edit")}</span>
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="py-12 text-center">
									<p className="text-muted-foreground">
										{t("admin:translations.no_resources")}
									</p>
								</div>
							)}
						</div>
						<EditorPanel
							editingItem={editingItem}
							resourceType="entitlement_feature"
							onSave={handleSave}
							onCancel={cancelEditing}
							isLoading={upsertEntitlementMutation.isPending}
						/>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	);
}
